/**
 * Prepara una foto en el navegador antes de subirla a Sharetribe.
 *
 * Sharetribe cobra la subida por tamaño (~214.000 créditos por GB, el 92% de lo que cuesta subir una
 * foto; ver docs/sharetribe-credits.md), y la foto tal como sale del móvil pesa de media 1,8 MB. La
 * web nunca enseña más de 1.600 px (la variante `-4x` de la ficha), así que todo lo que pase de ahí
 * se paga y no lo ve nadie. Aquí se reduce a `maxDimension` por el lado largo y se recodifica en
 * JPEG, que deja una foto de móvil en unos 300-500 KB.
 *
 * Es de mejor esfuerzo: si el navegador no sabe decodificar el fichero (HEIC fuera de Safari, por
 * ejemplo) o el resultado no sale más pequeño, se sube el original, como hasta ahora. De paso, el
 * canvas descarta los metadatos EXIF, incluida la ubicación GPS de la foto.
 */

// Sharetribe rechaza las subidas de más de 20 MB.
export const FLEX_UPLOAD_LIMIT_BYTES = 20 * 1024 * 1024;

// Por debajo de esto no compensa recodificar: el ahorro es pequeño y se pierde calidad.
const SMALL_ENOUGH_BYTES = 500 * 1024;

const JPEG = 'image/jpeg';
const QUALITY = 0.85;
// Solo si a QUALITY sigue sin caber en el límite de Sharetribe.
const FALLBACK_QUALITIES = [0.75, 0.65, 0.6];

/**
 * Tamaño de destino: el lado largo como mucho `maxDimension`, sin ampliar nunca. Exportada para
 * los tests.
 */
export const targetDimensions = (width, height, maxDimension) => {
  const scale = Math.min(1, maxDimension / Math.max(width, height));
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
};

/**
 * Si hace falta tocar el fichero: los GIF no (pueden ser animados) y lo que no es imagen tampoco.
 * Un fichero pequeño solo se toca si sus dimensiones pasan del máximo. Exportada para los tests.
 */
export const needsProcessing = (file, dimensions, maxDimension) => {
  if (!file?.type?.startsWith('image/') || file.type === 'image/gif') return false;
  if (file.size > SMALL_ENOUGH_BYTES) return true;
  return Math.max(dimensions.width, dimensions.height) > maxDimension;
};

const loadImageElement = file =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = e => {
      URL.revokeObjectURL(url);
      reject(e);
    };
    img.src = url;
  });

const canvasToBlob = (canvas, quality) =>
  new Promise(resolve => canvas.toBlob(resolve, JPEG, quality));

const jpegName = name => (name || 'image').replace(/\.[^.]+$/, '') + '.jpg';

/**
 * @param {File} file la foto elegida por el usuario
 * @param {{ maxDimension: number }} options lado largo máximo en píxeles
 * @returns {Promise<File>} la foto reducida, o el original si no se puede o no compensa
 */
export const prepareImageForUpload = async (file, { maxDimension }) => {
  if (typeof window === 'undefined' || typeof document === 'undefined') return file;
  if (!file?.type?.startsWith('image/') || file.type === 'image/gif') return file;

  try {
    const img = await loadImageElement(file);
    const natural = {
      width: img.naturalWidth || img.width,
      height: img.naturalHeight || img.height,
    };
    if (!natural.width || !natural.height || !needsProcessing(file, natural, maxDimension)) {
      return file;
    }

    const { width, height } = targetDimensions(natural.width, natural.height, maxDimension);
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return file;
    // Fondo blanco: JPEG no tiene transparencia y un PNG transparente saldría negro.
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, width, height);
    ctx.drawImage(img, 0, 0, width, height);

    let blob = await canvasToBlob(canvas, QUALITY);
    for (const quality of FALLBACK_QUALITIES) {
      if (blob && blob.size <= FLEX_UPLOAD_LIMIT_BYTES) break;
      blob = await canvasToBlob(canvas, quality);
    }

    if (!blob || blob.size >= file.size) return file;
    return new File([blob], jpegName(file.name), { type: JPEG, lastModified: Date.now() });
  } catch (e) {
    return file;
  }
};
